import React, { useState, useEffect, lazy, Suspense } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Routes, Route, Navigate } from 'react-router-dom';

import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Loader from './components/Loader';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';

// Lazy load below-the-fold components for better performance
const About = lazy(() => import('./components/About'));
const Services = lazy(() => import('./components/Services'));
const Portfolio = lazy(() => import('./components/Portfolio'));
const Careers = lazy(() => import('./components/Careers'));
const Contact = lazy(() => import('./components/Contact'));
const Footer = lazy(() => import('./components/Footer'));


// 🔐 Protected Route
const ProtectedRoute = ({ children }) => {
  const isAuthenticated =
    localStorage.getItem('isAdminAuthenticated') === 'true';

  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};


// 🚫 Prevent logged-in users from going back to login page
const PublicRoute = ({ children }) => {
  const isAuthenticated =
    localStorage.getItem('isAdminAuthenticated') === 'true';

  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};


// 🌐 Landing Page
const LandingPage = () => {
  const { scrollYProgress } = useScroll();

  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <>
      {/* Scroll Progress */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-brand-teal z-[100] origin-left"
        style={{ scaleX }}
      />

      <Navbar />

      <main>
        <Hero />
        <Suspense fallback={<div className="h-32 flex items-center justify-center"><div className="w-8 h-8 border-4 border-brand-teal border-t-transparent rounded-full animate-spin"></div></div>}>
          <About />
          <Services />
          <Portfolio />
          <Careers />
          <Contact />
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </>
  );
};


// 🚀 Main App
function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.classList.add('no-scroll');

    const timer = setTimeout(() => {
      setLoading(false);
      document.documentElement.classList.remove('no-scroll');
    }, 2800);

    return () => {
      clearTimeout(timer);
      document.documentElement.classList.remove('no-scroll');
    };
  }, []);

  return (
    <div className="bg-light-bg text-dark-text selection:bg-brand-teal/10 selection:text-brand-teal">
      
      {/* Loader */}
      {loading && <Loader />}

      {/* Routes */}
      <Routes>
        {/* Public Landing Page */}
        <Route path="/" element={<LandingPage />} />

        {/* Admin Login (blocked if already logged in) */}
        <Route
          path="/admin"
          element={
            <PublicRoute>
              <AdminLogin />
            </PublicRoute>
          }
        />

        {/* Protected Admin Pages */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Fallback Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;